import type { XYCoord } from 'dnd-core';
import { DOMAttributes, FC, PropsWithChildren, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { DndCardDragResult, DndCardItem, DndItemType } from 'src/schema/schema-drag-and-drop';

import classes from './DndCard.module.scss';

/**
 * 드래그 앤 드롭이 가능한 카드 컴포넌트
 * - 다중 선택 지원 (Cmd/Ctrl, Shift 키 사용)
 * - 삽입 위치 표시 (위쪽/아래쪽 라인)
 * - 그룹 간 이동 지원
 */
type DndCardProps = DOMAttributes<HTMLDivElement> & {
  /** 카드 아이템 데이터 */
  item: DndCardItem;
  /** 현재 카드가 선택된 상태인지 */
  isSelected: boolean;
  /** 현재 선택된 모든 카드들 */
  selectedItems: DndCardItem[];
  /** 카드 위쪽에 삽입 라인을 표시할지 */
  insertLineTop: boolean;
  /** 카드 아래쪽에 삽입 라인을 표시할지 */
  insertLineBottom: boolean;
  /** 모든 선택 상태를 해제하는 함수 */
  clearSelection: () => void;
  /** 드래그 시 삽입 위치를 설정하는 함수 */
  setInsertPosition: (groupId: string, hoverIndex: number, insertIndex: number) => void;
  /** 카드 선택 상태를 변경하는 함수 (다중 선택 지원) */
  onChangeSelectedItems: (index: number, groupId: string, cmdKey: boolean, shiftKey: boolean) => void;
  /** 드래그 완료 시 카드들을 이동시키는 함수 */
  moveItems: (dragResult: DndCardDragResult) => void;
  /** 추가 스타일 */
  style?: React.CSSProperties;
};

const DndCard: FC<PropsWithChildren<DndCardProps>> = ({
  item,
  isSelected,
  selectedItems,
  insertLineTop,
  insertLineBottom,
  clearSelection,
  setInsertPosition,
  onChangeSelectedItems,
  moveItems,
  children,
  style,
}) => {
  // DOM 요소 참조를 위한 ref
  const ref = useRef<HTMLDivElement | null>(null);

  // 드래그 기능 설정 - 이 카드를 드래그할 때의 처리
  const [{ isDragging }, drag] = useDrag<DndCardDragResult, any, { isDragging: boolean }>({
    type: DndItemType.CARD,
    item: () => {
      // 1. 선택된 카드를 움직이는 건지, 아니면 선택하지 않은 새로운 카드를 움직이는지 확인한다.
      const filteredSelectedItems = selectedItems.filter((i) => i.id !== item.id);
      // 선택된 카드에서 현재 카드를 뺀 길이과 빼지 않은 길이가 같으면, 현재 카드는 선택된 카드에 포함되지 않은 카드이다.
      const isCurrentItemSelected = selectedItems.length !== filteredSelectedItems.length;

      // 현재 카드가 선택된 상태면 선택된 모든 카드를, 아니면 현재 카드만 드래그
      const draggedItems = isCurrentItemSelected ? selectedItems : [item];

      // 현재 드래그중인 카드가 선택된 카드에 포함된게 아니라면, 선택된 카드를 모두 해제한다.
      if (!isCurrentItemSelected) {
        clearSelection();
      }

      // 2. 카드의 정렬 순서와는 별개로 드래그 중인 미리보기에서는
      // 사용자가 끌어당긴 카드를 가장 위로 보여줘야 하기 때문에, 별도의 stack을 만든다.
      const previewStack = [item, ...filteredSelectedItems];

      return { item, draggedItems, previewStack };
    },
    end: (item) => {
      // 드래그가 끝나면 카드들을 이동시키고 선택 상태 해제
      moveItems(item);
      clearSelection();
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(), // 현재 드래그 상태 수집
    }),
  });

  // 드롭 기능 설정 - 다른 카드가 이 위에 hover될 때의 처리
  const [{ hovered }, drop] = useDrop<void, any, { hovered: boolean }>({
    accept: DndItemType.CARD,
    // 선택된 카드가 들어갈 곳의 위치를 잡는다.
    hover: (_, monitor) => {
      // ref가 없으면 처리하지 않음
      if (!ref.current) return;

      const hoverIndex = item.index; // 현재 hover된 카드의 인덱스
      const hoverBoundingRect = ref.current?.getBoundingClientRect(); // hover된 카드의 영역
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2; // 카드 중간 지점
      const clientOffset = monitor.getClientOffset(); // 마우스 위치
      const mousePointerY = (clientOffset as XYCoord).y - hoverBoundingRect.top; // 카드 내 마우스 Y 위치

      // 마우스가 카드 중간보다 위에 있으면 현재 인덱스에, 아래에 있으면 다음 인덱스에 삽입
      const insertIndex = mousePointerY < hoverMiddleY ? hoverIndex : hoverIndex + 1;

      // 삽입 위치 설정 (삽입 라인 표시를 위해)
      setInsertPosition(item.groupId, hoverIndex, insertIndex);
    },
    collect: (monitor) => ({
      hovered: monitor.isOver(), // 현재 hover 상태 수집
    }),
  });

  // drag와 drop 기능을 모두 적용
  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`${classes.dndCard} ${isSelected ? classes.selected : ''}`}
      style={style}
      onClick={(e) => onChangeSelectedItems(item.index, item.groupId, e.metaKey, e.shiftKey)}>
      {/* 카드 위쪽에 삽입 라인 표시 */}
      {insertLineTop && hovered && <div className={classes.insertLineTop} />}

      {/* 드래그 중일 때 투명도 조절 */}
      <div style={{ opacity: isDragging ? 0.3 : 1 }}>{children}</div>

      {/* 카드 아래쪽에 삽입 라인 표시 */}
      {insertLineBottom && hovered && <div className={classes.insertLineBottom} />}
    </div>
  );
};

export default DndCard;
